import type {
  Conversation,
  Message,
  AttentionItem,
  User,
  Studio,
  UserRole,
  StudioRole,
  MessageProvenance,
  ChatHandlingContext,
  AutoSendCoverage,
  ConversationGroupBookingDraft,
  GroupBookingPolicyMode,
  ConversationMessagingSession,
  DraftProposedActionField,
  FirstTrialLinksBlockReason,
  EmailAttachment,
  EmailDeliveryStatus,
  PreviewDivergenceClass,
  CampaignLeadPreparation,
} from "@/types";
import { normalizeStudioBookingSource } from "@/lib/studio-data-filters";
import { normalizeAccountVertical } from "@/lib/vertical";
import { isSupportedUiLocale } from "@/lib/i18n/constants";
import { parseUnreadCount } from "@/lib/unread-count";
import { parseEscalationKind, parseEscalationReasonFamily } from "@/lib/escalation-why-chip";
import { parseLeadClassificationReason } from "@/lib/i18n/chat-display";

const intentMap: Record<string, Conversation["intent"]> = {
  booking: "Booking",
  rebooking: "Rebook",
  cancellation: "Cancel",
  info: "Info",
  schedule: "Schedule",
  account: "Account",
  other: "Other",
};

const channelMap: Record<string, Conversation["channel"]> = {
  instagram_dm: "instagram",
  whatsapp: "whatsapp",
  facebook: "facebook",
  email: "email",
};

/** Omit-empty string for optional wire keys (`customerPreviewMessage`, COPY-3 persist). */
function asNonEmptyTrimmedString(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function mapCampaignLeadPreparation(raw: unknown): CampaignLeadPreparation | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const value = raw as Record<string, unknown>;
  const preparationId = asNonEmptyTrimmedString(value.preparationId ?? value.preparation_id);
  const campaignId = asNonEmptyTrimmedString(value.campaignId ?? value.campaign_id);
  const campaignName = asNonEmptyTrimmedString(value.campaignName ?? value.campaign_name);
  const configVersion = asNonEmptyTrimmedString(value.configVersion ?? value.config_version);
  const createdAt = asNonEmptyTrimmedString(value.createdAt ?? value.created_at);
  const mode = value.mode === "manual" || value.mode === "auto_inert" ? value.mode : undefined;
  const status = value.status === "prepared_not_sent" || value.status === "active" || value.status === "closed"
    ? value.status : undefined;
  if (!preparationId || !campaignId || !configVersion || !createdAt || !mode || !status) return undefined;
  const hasEpisode = status === "active" || status === "closed";
  const episodeId = hasEpisode
    ? asNonEmptyTrimmedString(value.episodeId ?? value.episode_id)
    : undefined;
  const activatedAt = hasEpisode
    ? asNonEmptyTrimmedString(value.activatedAt ?? value.activated_at)
    : undefined;
  const rawEpisodePhase = value.episodePhase ?? value.episode_phase;
  const episodePhase = hasEpisode
    && (rawEpisodePhase === "awaiting_pack"
      || rawEpisodePhase === "pack_selected"
      || rawEpisodePhase === "link_ready"
      || rawEpisodePhase === "completed"
      || rawEpisodePhase === "suppressed")
    ? rawEpisodePhase
    : undefined;
  const selectedPackId = hasEpisode
    ? asNonEmptyTrimmedString(value.selectedPackId ?? value.selected_pack_id)
    : undefined;
  if (hasEpisode && (!episodeId || !activatedAt || !episodePhase)) return undefined;
  if (status === "active" && (episodePhase === "pack_selected" || episodePhase === "link_ready")
    && !selectedPackId) return undefined;
  const rawPurchaseEvidence = value.purchaseEvidence ?? value.purchase_evidence;
  if (hasEpisode && rawPurchaseEvidence !== undefined
    && rawPurchaseEvidence !== "none" && rawPurchaseEvidence !== "customer_claim"
    && rawPurchaseEvidence !== "verified") return undefined;
  const purchaseEvidence = rawPurchaseEvidence === "verified" ? "verified" as const
    : rawPurchaseEvidence === "customer_claim" ? "customer_claim" as const : "none" as const;
  const activePurchaseEvidence = purchaseEvidence === "verified" ? undefined : purchaseEvidence;
  if (status === "active" && !activePurchaseEvidence) return undefined;
  const closedAt = status === "closed"
    ? asNonEmptyTrimmedString(value.closedAt ?? value.closed_at) : undefined;
  const rawTerminalReason = value.terminalReason ?? value.terminal_reason;
  const terminalReason = status === "closed"
    && (rawTerminalReason === "purchase_verified" || rawTerminalReason === "member_discovered"
      || rawTerminalReason === "explicit_decline" || rawTerminalReason === "manager_takeover")
    ? rawTerminalReason : undefined;
  if (status === "closed" && (!closedAt || !terminalReason
    || (episodePhase !== "completed" && episodePhase !== "suppressed"))) return undefined;
  const packs = Array.isArray(value.packs)
    ? value.packs.flatMap((rawPack) => {
        if (!rawPack || typeof rawPack !== "object" || Array.isArray(rawPack)) return [];
        const pack = rawPack as Record<string, unknown>;
        const id = asNonEmptyTrimmedString(pack.id);
        const name = asNonEmptyTrimmedString(pack.name);
        const price = asNonEmptyTrimmedString(pack.price);
        const currency = asNonEmptyTrimmedString(pack.currency);
        if (!id || !name || !price || !currency) return [];
        return [{
          id,
          name,
          price,
          currency,
          conditions: asNonEmptyTrimmedString(pack.conditions) ?? "",
          purchaseUrl: asNonEmptyTrimmedString(pack.purchaseUrl ?? pack.purchase_url) ?? "",
        }];
      })
    : [];
  if (selectedPackId && !packs.some((pack) => pack.id === selectedPackId)) return undefined;
  return {
    preparationId,
    campaignId,
    ...(campaignName ? { campaignName } : {}),
    configVersion,
    mode,
    ...(status === "active"
      ? {
          status, episodeId: episodeId!, activatedAt: activatedAt!, purchaseEvidence: activePurchaseEvidence!,
          ...(episodePhase === "pack_selected" || episodePhase === "link_ready"
            ? { episodePhase, selectedPackId: selectedPackId! }
            : { episodePhase: "awaiting_pack" as const }),
        }
      : status === "closed"
        ? {
            status, episodeId: episodeId!, activatedAt: activatedAt!, closedAt: closedAt!,
            episodePhase: episodePhase as "completed" | "suppressed", terminalReason: terminalReason!,
            purchaseEvidence, ...(selectedPackId ? { selectedPackId } : {}),
          }
        : { status }),
    packs,
    createdAt,
  };
}

const PREVIEW_DIVERGENCE_CLASSES = [
  "equal",
  "tolerated_pending",
  "tolerated_already_held",
  "violation",
  "not_applicable",
] as const satisfies readonly PreviewDivergenceClass[];

function asPreviewDivergenceClass(raw: unknown): PreviewDivergenceClass | undefined {
  if (typeof raw !== "string") return undefined;
  return (PREVIEW_DIVERGENCE_CLASSES as readonly string[]).includes(raw)
    ? (raw as PreviewDivergenceClass)
    : undefined;
}

function mapEmailAttachments(raw: unknown): EmailAttachment[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const items = raw
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const o = entry as Record<string, unknown>;
      const id = typeof o.id === "string" ? o.id.trim() : "";
      const filename =
        typeof o.filename === "string"
          ? o.filename.trim()
          : typeof o.name === "string"
            ? o.name.trim()
            : "";
      if (!id || !filename) return null;
      const mimeType =
        typeof o.mimeType === "string"
          ? o.mimeType
          : typeof o.mime_type === "string"
            ? o.mime_type
            : undefined;
      const sizeBytes =
        typeof o.sizeBytes === "number"
          ? o.sizeBytes
          : typeof o.size_bytes === "number"
            ? o.size_bytes
            : undefined;
      const contentId =
        typeof o.contentId === "string"
          ? o.contentId.trim()
          : typeof o.content_id === "string"
            ? o.content_id.trim()
            : undefined;
      const url =
        typeof o.url === "string"
          ? o.url.trim()
          : typeof o.downloadUrl === "string"
            ? o.downloadUrl.trim()
            : typeof o.download_url === "string"
              ? o.download_url.trim()
              : undefined;
      return {
        id,
        filename,
        ...(mimeType ? { mimeType } : {}),
        ...(sizeBytes != null ? { sizeBytes } : {}),
        ...(contentId ? { contentId } : {}),
        ...(url ? { url } : {}),
      };
    })
    .filter((x): x is EmailAttachment => x !== null);
  return items.length > 0 ? items : undefined;
}

function mapEmailDeliveryStatus(raw: unknown): EmailDeliveryStatus | undefined {
  if (raw === "delivered" || raw === "failed" || raw === "pending") return raw;
  return undefined;
}

function readString(record: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function readStepIndex(details: Record<string, unknown>): number | null {
  const stepIndexRaw = details.stepIndex ?? details.step_index;
  if (typeof stepIndexRaw === "number" && Number.isFinite(stepIndexRaw)) return stepIndexRaw;
  if (typeof stepIndexRaw === "string" && /^\d+$/.test(stepIndexRaw.trim())) return Number(stepIndexRaw.trim());
  return null;
}

function parseActionLogWorkflow(details: Record<string, unknown>): {
  goal?: string;
  tier?: string;
  step?: number;
  stepDescription?: string;
} | null {
  const goal = readString(details, "goal", "goalName", "goal_name");
  const tier = readString(details, "tier", "tierName", "tier_name", "executionTier", "execution_tier");
  const stepDescription = readString(details, "stepDescription", "step_description", "description");
  const stepIndex = readStepIndex(details);
  if (!goal && !tier && stepIndex == null && !stepDescription) return null;
  return {
    goal: goal ?? undefined,
    tier: tier ?? undefined,
    step: stepIndex ?? undefined,
    stepDescription: stepDescription ?? undefined,
  };
}

function formatActionLogExecutionMeta(details: Record<string, unknown>): string[] {
  const fragments: string[] = [];
  const workflow = parseActionLogWorkflow(details);
  if (workflow?.goal) fragments.push(`goal: ${workflow.goal}`);
  if (workflow?.tier) fragments.push(`tier: ${workflow.tier}`);
  if (workflow?.stepDescription && workflow.step != null) fragments.push(`step ${workflow.step}: ${workflow.stepDescription}`);
  else if (workflow?.stepDescription) fragments.push(`step: ${workflow.stepDescription}`);
  else if (workflow?.step != null) fragments.push(`step: ${workflow.step}`);

  if (fragments.length > 0) return fragments;
  const steps = details.steps;
  if (Array.isArray(steps)) {
    const labels = steps.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0).slice(0, 3);
    if (labels.length > 0) fragments.push(`steps: ${labels.join(" | ")}`);
  }
  return fragments;
}

/**
 * Emits STRUCTURE, not a composed sentence (workplan B4 / PD-B4-TIER). The manager-facing label is
 * resolved at render time from the manager's own UI locale by
 * `src/lib/action-log-labels.ts:actionLogEntryText`; this mapper runs inside a TanStack query
 * function with no React context, so it cannot call `t()` itself. `text` therefore carries ONLY
 * the server-authored detail text and is empty when there is none.
 */
function mapActionLogEntry(entry: any): {
  icon: string;
  text: string;
  timestamp: string;
  actionType?: string;
  userName?: string;
  workflow?: {
    goal?: string;
    tier?: string;
    step?: number;
    stepDescription?: string;
  };
  draftText?: string;
  deliveredText?: string;
  previewDivergence?: PreviewDivergenceClass;
} {
  const actionType = typeof entry.actionType === "string" ? entry.actionType : undefined;
  const userName = typeof entry.userName === "string" ? entry.userName : undefined;
  let detailsText = typeof entry.details === "string" && entry.details.trim() ? entry.details.trim() : "";
  const detailsMeta =
    entry.details && typeof entry.details === "object"
      ? formatActionLogExecutionMeta(entry.details as Record<string, unknown>)
      : [];
  const workflow =
    entry.details && typeof entry.details === "object"
      ? parseActionLogWorkflow(entry.details as Record<string, unknown>) ?? undefined
      : undefined;
  if (!detailsText && entry.actionType === "auto_reply_sent" && entry.details && typeof entry.details === "object") {
    const d = entry.details as { intent?: string; confidence?: number; responsePreview?: string };
    const parts: string[] = [];
    if (d.intent) parts.push(`intent: ${d.intent}`);
    if (typeof d.confidence === "number") parts.push(`${Math.round(d.confidence * 100)}% confidence`);
    if (d.responsePreview) {
      const prev = String(d.responsePreview);
      parts.push(`"${prev.slice(0, 80)}${prev.length > 80 ? "…" : ""}"`);
    }
    detailsText = parts.length > 0 ? parts.join(" · ") : "";
  }
  const detailsWithMeta =
    detailsMeta.length > 0
      ? [detailsText, ...detailsMeta].filter((part) => part.trim().length > 0).join(" · ")
      : detailsText;
  const icon = entry.actionType === "status_changed" ? "warning" : "check";
  const ts = entry.timestamp ? new Date(entry.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  const persistDetails =
    entry.details && typeof entry.details === "object"
      ? (entry.details as Record<string, unknown>)
      : undefined;
  const draftText = asNonEmptyTrimmedString(persistDetails?.draftText ?? persistDetails?.draft_text);
  const deliveredText = asNonEmptyTrimmedString(
    persistDetails?.deliveredText ?? persistDetails?.delivered_text,
  );
  const previewDivergence = asPreviewDivergenceClass(
    persistDetails?.previewDivergence ?? persistDetails?.preview_divergence,
  );
  return {
    icon,
    text: detailsWithMeta,
    timestamp: ts,
    actionType,
    userName,
    workflow,
    ...(draftText ? { draftText } : {}),
    ...(deliveredText ? { deliveredText } : {}),
    ...(previewDivergence ? { previewDivergence } : {}),
  };
}

function mapIdentificationStatus(raw: Record<string, unknown>): Conversation["identificationStatus"] {
  const approvedRaw = raw.identityApprovedAt ?? raw.identity_approved_at;
  if (approvedRaw != null && String(approvedRaw).trim() !== "") {
    const t =
      approvedRaw instanceof Date ? approvedRaw.getTime() : new Date(String(approvedRaw)).getTime();
    if (!Number.isNaN(t)) return "identified";
  }
  const v = raw.identificationStatus ?? raw.identification_status;
  if (v === "identified" || v === "id_pending" || v === "unmatched" || v === "not_required") return v;
  return "id_pending";
}

function mapMessageProvenance(v: unknown): MessageProvenance | undefined {
  if (v === "customer" || v === "ai_orchestrator_auto" || v === "ai_draft_approved" || v === "team" || v === "session_nudge") return v;
  return undefined;
}

function mapAutoSendCoverage(v: unknown): AutoSendCoverage | undefined {
  if (v === "all" || v === "none" || v === "mixed") return v;
  return undefined;
}

function mapGroupBookingModeApplied(v: unknown): GroupBookingPolicyMode | undefined {
  if (v === "off" || v === "human_first" || v === "assistive_draft") return v;
  return undefined;
}

/** Normalize `groupBooking` from draft / proposed_action (handover §6.5). */
export function mapGroupBookingPayload(raw: unknown): ConversationGroupBookingDraft | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  if (o.active !== true) return undefined;
  const modeApplied = mapGroupBookingModeApplied(o.modeApplied ?? o.mode_applied);
  const signals = Array.isArray(o.signals)
    ? o.signals.filter((s): s is string => typeof s === "string")
    : undefined;
  const extractedPartySize =
    typeof o.extractedPartySize === "number"
      ? o.extractedPartySize
      : typeof o.extracted_party_size === "number"
        ? o.extracted_party_size
        : undefined;
  let ui: ConversationGroupBookingDraft["ui"] | undefined;
  const uiRaw = o.ui;
  if (uiRaw && typeof uiRaw === "object") {
    const u = uiRaw as Record<string, unknown>;
    const keys = u.checklistKeys ?? u.checklist_keys;
    const checklistKeys = Array.isArray(keys) ? keys.filter((k): k is string => typeof k === "string") : [];
    ui = {
      checklistKeys,
      overrideReasonKey:
        typeof u.overrideReasonKey === "string"
          ? u.overrideReasonKey
          : typeof u.override_reason_key === "string"
            ? u.override_reason_key
            : undefined,
    };
  }
  return { active: true, modeApplied, signals, extractedPartySize, ui };
}

function mapDraftProposedAction(pa: unknown): DraftProposedActionField {
  if (pa == null || pa === "") return "";
  if (typeof pa === "string") return pa;
  if (typeof pa === "object") {
    const o = pa as Record<string, unknown>;
    const description = typeof o.description === "string" ? o.description : "";
    const type = typeof o.type === "string" ? o.type : "";
    const detailsRaw = o.details;
    const details =
      detailsRaw && typeof detailsRaw === "object" && !Array.isArray(detailsRaw)
        ? (detailsRaw as Record<string, unknown>)
        : undefined;
    return { type, description, ...(details ? { details } : {}) };
  }
  return "";
}

function mapFirstTrialLinksBlockReason(v: unknown): FirstTrialLinksBlockReason | undefined {
  if (v === "missing_trial_registration_link" || v === "missing_studio_trial_booking_link") return v;
  return undefined;
}

function readFirstTrialLinksNotReady(...records: Array<Record<string, unknown> | null>): boolean {
  return records.some((record) => record?.firstTrialLinksNotReady === true || record?.first_trial_links_not_ready === true);
}

/** UX-63 Ship 2: schema is `z.literal(true).optional()` — only an explicit `true` on either
 * the proposedAction object or the AIDraft top level means "render the banner". Absent or
 * (schema-invalid) `false` must both resolve to no-clash; never branch on an explicit `false`. */
function readCompletedWriteDenialClash(...records: Array<Record<string, unknown> | null>): boolean {
  return records.some(
    (record) => record?.completedWriteDenialClash === true || record?.completed_write_denial_clash === true,
  );
}

export function mapConversationMessagingSession(raw: unknown): ConversationMessagingSession | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const health = o.health;
  if (health !== "open" && health !== "expiring_soon" && health !== "expired" && health !== "unknown") return undefined;
  const tr = o.timeRemainingSec ?? o.time_remaining_sec;
  let timeRemainingSec: number | null = null;
  if (typeof tr === "number" && Number.isFinite(tr)) timeRemainingSec = tr;
  else if (typeof tr === "string" && tr.trim() !== "") {
    const n = parseInt(tr, 10);
    timeRemainingSec = Number.isNaN(n) ? null : n;
  }
  const windowEndsAt =
    typeof o.windowEndsAt === "string"
      ? o.windowEndsAt
      : typeof o.window_ends_at === "string"
        ? o.window_ends_at
        : null;
  const unanswered = o.unanswered;
  const nudgeLastSentAt =
    typeof o.nudgeLastSentAt === "string"
      ? o.nudgeLastSentAt
      : typeof o.nudge_last_sent_at === "string"
        ? o.nudge_last_sent_at
        : undefined;
  return {
    windowEndsAt: windowEndsAt ?? null,
    health,
    timeRemainingSec,
    ...(typeof unanswered === "boolean" ? { unanswered } : {}),
    ...(nudgeLastSentAt ? { nudgeLastSentAt } : {}),
  };
}

export function mapConversation(raw: any): Conversation {
  const customer = (raw.customer ?? {}) as Record<string, unknown>;
  const upcomingClassesRaw = (raw.upcomingClasses ??
    raw.upcoming_classes ??
    customer.upcomingClasses ??
    customer.upcoming_classes ??
    []) as unknown;
  const upcomingClasses = Array.isArray(upcomingClassesRaw)
    ? upcomingClassesRaw
        .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object")
        .map((entry) => ({
          id: typeof entry.id === "string" ? entry.id : undefined,
          startsAt:
            typeof entry.startsAt === "string"
              ? entry.startsAt
              : typeof entry.starts_at === "string"
                ? entry.starts_at
                : typeof entry.time === "string"
                  ? entry.time
                  : undefined,
          name:
            typeof entry.name === "string"
              ? entry.name
              : typeof entry.className === "string"
                ? entry.className
                : typeof entry.class_name === "string"
                  ? entry.class_name
                  : undefined,
          instructor:
            typeof entry.instructor === "string"
              ? entry.instructor
              : typeof entry.trainer === "string"
                ? entry.trainer
                : undefined,
        }))
    : undefined;
  const documentsRaw = (raw.documents ??
    raw.attachments ??
    raw.files ??
    customer.documents ??
    customer.attachments ??
    []) as unknown;
  const documents = Array.isArray(documentsRaw)
    ? documentsRaw
        .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object")
        .map((entry) => {
          const name =
            typeof entry.name === "string"
              ? entry.name
              : typeof entry.fileName === "string"
                ? entry.fileName
                : typeof entry.file_name === "string"
                  ? entry.file_name
                  : typeof entry.filename === "string"
                    ? entry.filename
                    : "";
          if (!name.trim()) return null;
          const senderRaw =
            typeof entry.sender === "string"
              ? entry.sender
              : typeof entry.source === "string"
                ? entry.source
                : undefined;
          const sender: "customer" | "staff" | undefined =
            senderRaw === "customer" ? "customer" : senderRaw === "staff" ? "staff" : undefined;
          return {
            id: typeof entry.id === "string" ? entry.id : undefined,
            name,
            mimeType:
              typeof entry.mimeType === "string"
                ? entry.mimeType
                : typeof entry.mime_type === "string"
                  ? entry.mime_type
                  : typeof entry.contentType === "string"
                    ? entry.contentType
                    : undefined,
            url:
              typeof entry.url === "string"
                ? entry.url
                : typeof entry.href === "string"
                  ? entry.href
                  : undefined,
            sender,
          };
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    : undefined;
  const actionLog = Array.isArray(raw.actionLog) ? raw.actionLog.map(mapActionLogEntry) : undefined;
  const handlingContext = raw.handlingContext as ChatHandlingContext | undefined;
  return {
    id: raw.id,
    customerProfileId:
      raw.customerProfileId != null && String(raw.customerProfileId).trim() !== ""
        ? String(raw.customerProfileId).trim()
        : raw.customer_profile_id != null && String(raw.customer_profile_id).trim() !== ""
          ? String(raw.customer_profile_id).trim()
          : null,
    customerId:
      raw.customerId != null && String(raw.customerId).trim() !== ""
        ? String(raw.customerId).trim()
        : raw.customer_id != null && String(raw.customer_id).trim() !== ""
          ? String(raw.customer_id).trim()
          : undefined,
    channelSenderId:
      typeof raw.channelSenderId === "string"
        ? raw.channelSenderId
        : typeof raw.channel_sender_id === "string"
          ? raw.channel_sender_id
          : undefined,
    customerName: raw.customerName || raw.customer?.name || "Unknown",
    customerAvatar: raw.customerAvatarUrl || raw.customer?.avatar || undefined,
    customerPhone:
      typeof raw.customerPhone === "string"
        ? raw.customerPhone
        : typeof raw.customer_phone === "string"
          ? raw.customer_phone
          : typeof customer.phone === "string"
            ? customer.phone
            : undefined,
    customerInstagram:
      typeof raw.customerInstagram === "string"
        ? raw.customerInstagram
        : typeof raw.customer_instagram === "string"
          ? raw.customer_instagram
          : typeof customer.instagram === "string"
            ? customer.instagram
            : typeof customer.instagramHandle === "string"
              ? customer.instagramHandle
              : undefined,
    customerEmail:
      typeof raw.customerEmail === "string"
        ? raw.customerEmail
        : typeof raw.customer_email === "string"
          ? raw.customer_email
          : typeof customer.email === "string"
            ? customer.email
            : undefined,
    customerSecondaryId:
      typeof raw.customerSecondaryId === "string"
        ? raw.customerSecondaryId
        : typeof raw.customer_secondary_id === "string"
          ? raw.customer_secondary_id
          : typeof raw.secondaryExternalId === "string"
            ? raw.secondaryExternalId
            : typeof raw.secondary_external_id === "string"
              ? raw.secondary_external_id
              : typeof customer.secondaryId === "string"
                ? customer.secondaryId
                : typeof customer.secondary_id === "string"
                  ? customer.secondary_id
                  : typeof customer.secondaryExternalId === "string"
                    ? customer.secondaryExternalId
                    : typeof customer.secondary_external_id === "string"
                      ? customer.secondary_external_id
                      : undefined,
    upcomingClasses,
    documents: documents && documents.length > 0 ? documents : undefined,
    channel: channelMap[raw.channel] || "whatsapp",
    subject: typeof raw.subject === "string" && raw.subject.trim() ? raw.subject.trim() : undefined,
    studioId: raw.studioId || raw.studio_id || "",
    studioName: raw.studioName || raw.studio_name || "",
    intent: intentMap[raw.intentType || raw.intent] || "Other",
    status: raw.status,
    ...(() => {
      const reason = raw.followupReason ?? raw.followup_reason;
      return reason === "campaign_lead_quiet" || reason === "processing_blocked"
        ? { followupReason: reason }
        : {};
    })(),
    ...(() => {
      // A1 / PM-ESC-3 — closed vocabulary only (deny-by-default).
      const kind =
        parseEscalationKind(raw.escalationKind) ?? parseEscalationKind(raw.escalation_kind);
      const family =
        parseEscalationReasonFamily(raw.escalationReasonFamily) ??
        parseEscalationReasonFamily(raw.escalation_reason_family);
      return {
        ...(kind ? { escalationKind: kind } : {}),
        ...(family ? { escalationReasonFamily: family } : {}),
      };
    })(),
    ...(() => {
      // WP-FE-C-LEAD (UC-8, dark-until-data) — sibling arm to the escalation pair above.
      // `leadOrigin` is presence-gated only (any non-empty string renders the badge); the
      // classification reason is closed-vocabulary and denies-by-default on unknown.
      const leadOriginRaw = raw.leadOrigin ?? raw.lead_origin;
      const leadOrigin =
        typeof leadOriginRaw === "string" && leadOriginRaw.trim() ? leadOriginRaw.trim() : undefined;
      const leadClassificationReason =
        parseLeadClassificationReason(raw.leadClassificationReason) ??
        parseLeadClassificationReason(raw.lead_classification_reason);
      return {
        ...(leadOrigin ? { leadOrigin } : {}),
        ...(leadClassificationReason ? { leadClassificationReason } : {}),
      };
    })(),
    ...(() => {
      const preparation = mapCampaignLeadPreparation(
        raw.campaignLeadPreparation ?? raw.campaign_lead_preparation,
      );
      return preparation ? { campaignLeadPreparation: preparation } : {};
    })(),
    lastMessage: raw.lastMessagePreview || raw.lastMessage || "",
    timestamp: raw.lastActivityAt || raw.timestamp || raw.createdAt || "",
    unreadCount: parseUnreadCount(raw),
    unread: raw.hasUnread === true || raw.has_unread === true || !!raw.unread || parseUnreadCount(raw) > 0,
    identificationStatus: mapIdentificationStatus(raw),
    identityApprovedAt: (() => {
      const a = raw.identityApprovedAt ?? raw.identity_approved_at;
      if (a == null) return undefined;
      if (a instanceof Date && !Number.isNaN(a.getTime())) return a.toISOString();
      const s = String(a).trim();
      if (!s) return undefined;
      const t = new Date(s).getTime();
      return Number.isNaN(t) ? undefined : new Date(t).toISOString();
    })(),
    autoHandle: raw.status === "ai_handled",
    ...(raw.groupBookingActive === true || raw.group_booking_active === true ? { groupBookingActive: true as const } : {}),
    draftData: (() => {
      const aiDraftRaw = raw.aiDraft ?? raw.ai_draft;
      if (!aiDraftRaw || typeof aiDraftRaw !== "object") return undefined;
      const aiDraft = aiDraftRaw as Record<string, unknown>;
      const proposedActionRaw = aiDraft.proposedAction ?? aiDraft.proposed_action;
      const paObj =
        proposedActionRaw !== null && typeof proposedActionRaw === "object"
          ? (proposedActionRaw as Record<string, unknown>)
          : null;
      const gbFromPa =
        paObj && "groupBooking" in paObj
          ? paObj.groupBooking
          : paObj && "group_booking" in paObj
            ? paObj.group_booking
            : undefined;
      const gbTop = aiDraft.groupBooking ?? aiDraft.group_booking;
      const groupBooking = mapGroupBookingPayload(gbFromPa ?? gbTop);
      const firstTrialFromPa =
        paObj?.firstTrialScenario === "no_platform_account" || paObj?.firstTrialScenario === "has_platform_account"
          ? paObj.firstTrialScenario
          : undefined;
      const firstTrialFromDraft =
        aiDraft.firstTrialScenario === "no_platform_account" || aiDraft.firstTrialScenario === "has_platform_account"
          ? aiDraft.firstTrialScenario
          : undefined;
      const firstTrialLinksBlockReason =
        mapFirstTrialLinksBlockReason(paObj?.firstTrialLinksBlockReason) ??
        mapFirstTrialLinksBlockReason(paObj?.first_trial_links_block_reason) ??
        mapFirstTrialLinksBlockReason(aiDraft.firstTrialLinksBlockReason) ??
        mapFirstTrialLinksBlockReason(aiDraft.first_trial_links_block_reason);
      const firstTrialLinksNotReady = readFirstTrialLinksNotReady(paObj, aiDraft);
      const completedWriteDenialClash = readCompletedWriteDenialClash(paObj, aiDraft);
      // BE-275 Phase 4 — only the two literals pass; anything else ⇒ the field is omitted.
      const heldCancelWindowStateRaw = aiDraft.heldCancelWindowState ?? aiDraft.held_cancel_window_state;
      const heldCancelWindowState =
        heldCancelWindowStateRaw === "early" || heldCancelWindowStateRaw === "late"
          ? heldCancelWindowStateRaw
          : undefined;
      const draftIdRaw = aiDraft.id;
      // BE-121 Phase 2 claim fields (FE-34). Rendered as-is; never recomputed on the client.
      const claimStateRaw = aiDraft.claimState ?? aiDraft.claim_state;
      const claimState =
        claimStateRaw === "idle" || claimStateRaw === "live" || claimStateRaw === "stale"
          ? claimStateRaw
          : undefined;
      const claimHolderRaw = aiDraft.claimHolder ?? aiDraft.claim_holder;
      const claimHolderPresent = "claimHolder" in aiDraft || "claim_holder" in aiDraft;
      const claimHolder = (() => {
        if (!claimHolderRaw || typeof claimHolderRaw !== "object") return null;
        const h = claimHolderRaw as Record<string, unknown>;
        const userId = h.userId ?? h.user_id;
        const name = h.name;
        if (typeof userId !== "string" || !userId.trim()) return null;
        if (typeof name !== "string" || !name.trim()) return null;
        return { userId, name };
      })();
      const claimLastRefreshAt = (() => {
        const a = aiDraft.claimLastRefreshAt ?? aiDraft.claim_last_refresh_at;
        if (a == null) return undefined;
        if (a instanceof Date && !Number.isNaN(a.getTime())) return a.toISOString();
        const s = String(a).trim();
        if (!s) return undefined;
        const ts = new Date(s).getTime();
        return Number.isNaN(ts) ? undefined : new Date(ts).toISOString();
      })();
      const customerPreviewMessage = asNonEmptyTrimmedString(
        aiDraft.customerPreviewMessage ?? aiDraft.customer_preview_message,
      );
      // BE-261 D15/D16 — only a literal `true` counts; absent/false/other ⇒ the field is omitted.
      const customerWroteAgain = (aiDraft.customerWroteAgain ?? aiDraft.customer_wrote_again) === true;
      const staleReleased = (aiDraft.staleReleased ?? aiDraft.stale_released) === true;
      return {
        ...(typeof draftIdRaw === "string" && draftIdRaw.trim() ? { id: draftIdRaw } : {}),
        content: String(aiDraft.proposedMessage ?? aiDraft.text ?? aiDraft.proposed_message ?? ""),
        proposedAction: mapDraftProposedAction(proposedActionRaw),
        ...(customerPreviewMessage ? { customerPreviewMessage } : {}),
        ...(customerWroteAgain ? { customerWroteAgain: true as const } : {}),
        ...(staleReleased
          ? {
              staleReleased: true as const,
              staleReleasedReason:
                typeof (aiDraft.staleReleasedReason ?? aiDraft.stale_released_reason) === "string"
                  ? String(aiDraft.staleReleasedReason ?? aiDraft.stale_released_reason)
                  : "head_released",
            }
          : {}),
        ...(firstTrialFromDraft ?? firstTrialFromPa
          ? { firstTrialScenario: firstTrialFromDraft ?? firstTrialFromPa }
          : {}),
        ...(firstTrialLinksNotReady ? { firstTrialLinksNotReady: true } : {}),
        ...(firstTrialLinksBlockReason ? { firstTrialLinksBlockReason } : {}),
        ...(completedWriteDenialClash ? { completedWriteDenialClash: true } : {}),
        ...(heldCancelWindowState ? { heldCancelWindowState } : {}),
        blocked: typeof aiDraft.blocked === "boolean" ? aiDraft.blocked : false,
        // BE-281 — passed only when boolean; absent keeps Send off whenever `blocked`.
        ...(typeof (aiDraft.sendBlocked ?? aiDraft.send_blocked) === "boolean"
          ? { sendBlocked: (aiDraft.sendBlocked ?? aiDraft.send_blocked) as boolean }
          : {}),
        blockedReason:
          typeof aiDraft.blockedReason === "string"
            ? aiDraft.blockedReason
            : typeof aiDraft.blocked_reason === "string"
              ? aiDraft.blocked_reason
              : undefined,
        groupBooking,
        ...(claimState ? { claimState } : {}),
        ...(claimHolderPresent ? { claimHolder } : {}),
        ...(claimLastRefreshAt ? { claimLastRefreshAt } : {}),
      };
    })(),
    actionLog,
    handlingContext:
      handlingContext &&
      typeof handlingContext === "object" &&
      typeof handlingContext.draftMode === "boolean" &&
      typeof handlingContext.automatedReplyCustomerAttribution === "string"
        ? ({
            ...handlingContext,
            autoSendCoverage:
              mapAutoSendCoverage((handlingContext as ChatHandlingContext).autoSendCoverage) ??
              ((handlingContext as ChatHandlingContext).draftMode ? "mixed" : "all"),
          } as ChatHandlingContext)
        : undefined,
    ...(() => {
      const session = mapConversationMessagingSession(raw.session);
      return session ? { session } : {};
    })(),
  };
}

function looksLikeHtml(text: string): boolean {
  return /<\/?[a-z][\s\S]*?>/i.test(text);
}

export function mapMessage(raw: any, conversationId: string): Message {
  const apiSender = String(raw.sender ?? "");
  const direction = String(raw.direction ?? "");
  const bodyHtmlEarly =
    typeof raw.bodyHtml === "string"
      ? raw.bodyHtml
      : typeof raw.body_html === "string"
        ? raw.body_html
        : undefined;
  const isEmailShape = Boolean(bodyHtmlEarly || (typeof raw.from === "string" && raw.from.trim()));
  const sender: Message["sender"] =
    apiSender === "customer" || (isEmailShape && direction === "inbound")
      ? "customer"
      : apiSender === "ai"
        ? "ai"
        : apiSender === "system"
          ? "system"
          : "human";
  const sn = raw.senderName ?? raw.sender_name;
  const senderLabel = typeof sn === "string" && sn.trim() ? sn.trim() : undefined;
  const mp = raw.messageProvenance ?? raw.message_provenance;
  const rawContent = typeof raw.content === "string" ? raw.content.trim() : "";
  let bodyHtml = bodyHtmlEarly;
  if (!bodyHtml && isEmailShape && rawContent && looksLikeHtml(rawContent)) {
    bodyHtml = rawContent;
  }
  const bodyText =
    typeof raw.bodyText === "string"
      ? raw.bodyText
      : typeof raw.body_text === "string"
        ? raw.body_text
        : undefined;
  const from = typeof raw.from === "string" && raw.from.trim() ? raw.from.trim() : undefined;
  const toRaw = raw.to;
  const to = Array.isArray(toRaw) ? toRaw.filter((x): x is string => typeof x === "string" && x.trim().length > 0) : undefined;
  const ccRaw = raw.cc;
  // BE-75 (API-SPEC §4.4): server emits cc as { name?, email } participants; older
  // payloads/mocks may carry plain strings. Map both to display strings.
  const cc = Array.isArray(ccRaw)
    ? ccRaw
        .map((x): string | null => {
          if (typeof x === "string") return x.trim() || null;
          if (x && typeof x === "object" && typeof (x as { email?: unknown }).email === "string") {
            const email = ((x as { email: string }).email ?? "").trim();
            if (!email) return null;
            const nameRaw = (x as { name?: unknown }).name;
            const name = typeof nameRaw === "string" ? nameRaw.trim() : "";
            return name && name.toLowerCase() !== email.toLowerCase() ? `${name} <${email}>` : email;
          }
          return null;
        })
        .filter((x): x is string => Boolean(x))
    : undefined;
  const subject = typeof raw.subject === "string" && raw.subject.trim() ? raw.subject.trim() : undefined;
  const attachments = mapEmailAttachments(raw.attachments);
  const deliveryStatus =
    mapEmailDeliveryStatus(raw.deliveryStatus ?? raw.delivery_status) ??
    (raw.status === "failed" ? ("failed" as const) : undefined);
  return {
    id: raw.id,
    conversationId,
    sender,
    senderLabel,
    messageProvenance: mapMessageProvenance(mp),
    content: bodyText || raw.content || raw.text || "",
    timestamp: raw.sentAt || raw.sent_at || raw.timestamp || "",
    isEdited: raw.isEdited === true || raw.is_edited === true ? true : undefined,
    editedAt:
      typeof (raw.editedAt ?? raw.edited_at) === "string"
        ? (raw.editedAt ?? raw.edited_at)
        : undefined,
    ...(from ? { from } : {}),
    ...(to && to.length > 0 ? { to } : {}),
    ...(cc && cc.length > 0 ? { cc } : {}),
    ...(subject ? { subject } : {}),
    ...(bodyHtml ? { bodyHtml } : {}),
    ...(bodyText ? { bodyText } : {}),
    ...(deliveryStatus ? { deliveryStatus } : {}),
    ...(attachments ? { attachments } : {}),
  };
}

export function mapAttentionItem(raw: any): AttentionItem {
  const typ = String(raw.type ?? "");
  const type: AttentionItem["type"] =
    typ === "escalated" ? "escalated" : typ === "session_expiring" ? "session_expiring" : "draft_ready";
  return {
    id: raw.conversationId || raw.id || "",
    type,
    conversationId: raw.conversationId || raw.id || "",
    customerName: raw.customerName || "Unknown",
    channel: channelMap[raw.channel] || "whatsapp",
    intent: intentMap[raw.intentType || raw.intent] || "Other",
    messagePreview: raw.previewText || raw.lastMessagePreview || raw.lastMessage || "",
    studioName: raw.studioName || "",
    timestamp: raw.waitingSince || raw.lastActivityAt || raw.timestamp || "",
    actionSummary: typeof raw.actionSummary === "string" && raw.actionSummary.length > 0 ? raw.actionSummary : undefined,
    studioId: typeof raw.studioId === "string" && raw.studioId.trim() ? raw.studioId.trim() : undefined,
    sessionWindowEndsAt: typeof raw.sessionWindowEndsAt === "string" ? raw.sessionWindowEndsAt : undefined,
  };
}

function mapConnectionStatus(v: string): { status: "connected" | "error" | "not_connected" } {
  if (v === "connected") return { status: "connected" };
  if (v === "error") return { status: "error" };
  return { status: "not_connected" };
}

function roleLabelFromStudioList(r: string | undefined): UserRole {
  const x = String(r ?? "staff").toLowerCase();
  if (x === "owner") return "Owner";
  if (x === "manager") return "Manager";
  return "Staff";
}

/** Map GET /studios response to Studio[], roles per studio, and currentStudioId. `studioRoles` must replace client state on refresh so deleted studios do not leave stale Owner/Manager rows. */
export function mapStudioListResponse(data: any): {
  studios: Studio[];
  studioRoles: StudioRole[];
  currentStudioId: string | "all" | null;
} {
  const list = data?.studios ?? [];
  const studios: Studio[] = list.map((s: any) => {
    const conn = s.connections ?? {};
    return {
      id: s.id,
      name: s.name,
      ownerUserId:
        (typeof s.ownerUserId === "string" && s.ownerUserId.trim().length > 0
          ? s.ownerUserId
          : typeof s.owner_user_id === "string" && s.owner_user_id.trim().length > 0
            ? s.owner_user_id
            : undefined),
      description: s.description ?? undefined,
      address: typeof s.address === "string" && s.address.trim() ? s.address.trim() : undefined,
      bookingSourceKey: normalizeStudioBookingSource(typeof s.source === "string" ? s.source : null),
      onboardingComplete: s.onboardingComplete ?? true,
      connections: {
        instagram: mapConnectionStatus(conn.instagram ?? "disconnected"),
        whatsapp: mapConnectionStatus(conn.whatsapp ?? "disconnected"),
        bookingPlatform: mapConnectionStatus(conn.bookingPlatform ?? "disconnected"),
      },
    };
  });
  const studioRoles: StudioRole[] = list
    .filter((s: any) => s && typeof s.id === "string" && s.id.length > 0)
    .map((s: any) => ({
      studioId: String(s.id),
      role: roleLabelFromStudioList(typeof s.role === "string" ? s.role : undefined),
    }));
  const currentStudioId = data?.currentStudioId ?? null;
  return { studios, studioRoles, currentStudioId };
}

export function mapLoginResponseToUser(data: any): User {
  const u = data?.user;
  if (!u || typeof u !== "object") {
    throw new Error("Invalid user payload");
  }

  const rawStudios = Array.isArray(u.studios) ? u.studios : [];
  const studios: Studio[] = rawStudios.map((s: any) => {
    const conn = s.connections ?? {};
    return {
      id: s.id,
      name: s.name,
      ownerUserId:
        (typeof s.ownerUserId === "string" && s.ownerUserId.trim().length > 0
          ? s.ownerUserId
          : typeof s.owner_user_id === "string" && s.owner_user_id.trim().length > 0
            ? s.owner_user_id
            : undefined),
      description: s.description ?? "",
      address: typeof s.address === "string" && s.address.trim() ? s.address.trim() : undefined,
      bookingSourceKey: normalizeStudioBookingSource(typeof s.source === "string" ? s.source : null),
      onboardingComplete: s.onboardingComplete ?? true,
      connections: {
        instagram: mapConnectionStatus(conn.instagram ?? "disconnected"),
        whatsapp: mapConnectionStatus(conn.whatsapp ?? "disconnected"),
        bookingPlatform: mapConnectionStatus(conn.bookingPlatform ?? "disconnected"),
      },
    };
  });

  const roleLabel = (r: string): UserRole => {
    const x = String(r).toLowerCase();
    if (x === "owner") return "Owner";
    if (x === "manager") return "Manager";
    return "Staff";
  };

  const studioRoles =
    Array.isArray(u.studioRoles) && u.studioRoles.length > 0
      ? u.studioRoles
          .filter((sr: any) => sr && typeof sr.studioId === "string")
          .map((sr: any) => ({
            studioId: sr.studioId,
            role: roleLabel(typeof sr.role === "string" ? sr.role : "staff"),
          }))
      : rawStudios.map((s: any) => ({
          studioId: String(s.id ?? ""),
          role: roleLabel(typeof s.role === "string" ? s.role : "owner"),
        }));

  const email = typeof u.email === "string" ? u.email : "";
  const id = u.id != null && String(u.id).length > 0 ? String(u.id) : "";
  if (!id) {
    throw new Error("Invalid user payload");
  }

  const nameFromEmail = email.includes("@") ? email.split("@")[0] : "User";
  const name =
    typeof u.name === "string" && u.name.trim().length > 0 ? u.name.trim() : nameFromEmail;
  const accountVertical = normalizeAccountVertical(u.accountVertical ?? u.account_vertical);
  const rawPreferredUiLocale = u.preferredUiLocale ?? u.preferred_ui_locale;
  const preferredUiLocale = typeof rawPreferredUiLocale === "string" && isSupportedUiLocale(rawPreferredUiLocale)
    ? rawPreferredUiLocale
    : null;

  return {
    id,
    email,
    name,
    emailVerified: u.emailVerified ?? true,
    onboardingComplete: u.onboardingComplete ?? true,
    accountVertical,
    preferredUiLocale,
    studios,
    studioRoles,
  };
}
